import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  getRegionList, 
  createRegion, 
  updateRegion, 
  deleteRegion,
  importExcel,
  getRegionTree
} from '../../api/region';
import { message } from 'antd';

// 初始状态
const initialState = {
  regionList: [],
  total: 0,
  loading: false,
  currentPage: 1,
  pageSize: 10,
  importLoading: false,
  importResult: null,
  regionTree: [],
  treeLoading: false
};

// 异步action - 获取地区列表
export const fetchRegionList = createAsyncThunk(
  'region/fetchRegionList',
  async ({ page, pageSize, queryParams }, { rejectWithValue }) => {
    try {
      const response = await getRegionList({ page, pageSize, ...queryParams });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 异步action - 创建地区
export const addRegion = createAsyncThunk(
  'region/addRegion',
  async (regionData, { rejectWithValue, dispatch }) => {
    try {
      const response = await createRegion(regionData);
      message.success('创建地区成功');
      // 刷新列表
      dispatch(fetchRegionList({ page: 1, pageSize: 10 }));
      return response.data;
    } catch (error) {
      message.error('创建地区失败');
      return rejectWithValue(error.response.data);
    }
  }
);

// 异步action - 更新地区
export const modifyRegion = createAsyncThunk(
  'region/modifyRegion',
  async (regionData, { rejectWithValue, dispatch }) => {
    try {
      const response = await updateRegion(regionData);
      message.success('更新地区成功');
      // 刷新列表
      dispatch(fetchRegionList({ page: 1, pageSize: 10 }));
      return response.data;
    } catch (error) {
      message.error('更新地区失败');
      return rejectWithValue(error.response.data);
    }
  }
);

// 异步action - 删除地区
export const removeRegion = createAsyncThunk(
  'region/removeRegion',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await deleteRegion(id);
      message.success('删除地区成功');
      // 刷新列表
      dispatch(fetchRegionList({ page: 1, pageSize: 10 }));
      return response.data;
    } catch (error) {
      message.error('删除地区失败');
      return rejectWithValue(error.response.data);
    }
  }
);

// 异步action - 导入Excel
export const importRegionExcel = createAsyncThunk(
  'region/importExcel',
  async (file, { rejectWithValue, dispatch }) => {
    try {
      const response = await importExcel(file);
      const result = response.data;
      
      // 显示导入结果
      if (result.failureCount > 0) {
        message.warning(`导入完成，成功：${result.successCount}条，失败：${result.failureCount}条`);
      } else {
        message.success(`导入成功，共导入${result.successCount}条数据`);
      }

      // 刷新列表
      dispatch(fetchRegionList({ page: 1, pageSize: 10 }));
      return result;
    } catch (error) {
      message.error('导入失败：' + (error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data || error);
    }
  }
);

// 异步action - 获取地区树
export const fetchRegionTree = createAsyncThunk(
  'region/fetchTree',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRegionTree();
      return response.data;
    } catch (error) {
      message.error('获取地区树失败');
      return rejectWithValue(error.response?.data || error);
    }
  }
);

const regionSlice = createSlice({
  name: 'region',
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取地区列表
      .addCase(fetchRegionList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRegionList.fulfilled, (state, action) => {
        state.loading = false;
        state.regionList = action.payload.records;
        state.total = action.payload.total;
      })
      .addCase(fetchRegionList.rejected, (state) => {
        state.loading = false;
      })
      // 导入Excel
      .addCase(importRegionExcel.pending, (state) => {
        state.importLoading = true;
      })
      .addCase(importRegionExcel.fulfilled, (state, action) => {
        state.importLoading = false;
        state.importResult = action.payload;
      })
      .addCase(importRegionExcel.rejected, (state) => {
        state.importLoading = false;
      })
      // 获取地区树
      .addCase(fetchRegionTree.pending, (state) => {
        state.treeLoading = true;
      })
      .addCase(fetchRegionTree.fulfilled, (state, action) => {
        state.treeLoading = false;
        state.regionTree = action.payload;
      })
      .addCase(fetchRegionTree.rejected, (state) => {
        state.treeLoading = false;
      });
  },
});

export const { setCurrentPage, setPageSize } = regionSlice.actions;

export default regionSlice.reducer;